import { GetDataListResponse, GetPageParams } from 'types/api';
import { BillingAddress, ClosetPlanType } from 'types/plansales/billing';
import { CheckObjectValue, Nullable } from 'types/common';
import { CouponReason, CouponStatus, CouponType } from 'constants/member/connect';
import { ServiceType } from 'types/common/service';

export enum OrderType {
  NONE = 0,
  ONLINE,
  BACK_ORDER,
}

export enum CouponFormField {
  TYPE = 'couponType',
  AMOUNT = 'amount',
  RATE = 'rate',
  DESCRIPTION = 'description',
  REASON = 'couponContext',
}

export type PostCouponInformationParams = {
  userId: string;
  type: CouponType;
  amount: number;
  dateInterval: string[];
  reason: CouponReason;
  description: string;
  reFetch?: boolean;
};

export type Plan = {
  planType: ClosetPlanType;
  planStartDate: string;
  planEndDate: string;
  orderType: OrderType;
};
export interface CurrentPlan extends Plan {
  invalidDate: boolean;
  seq: number;
}

/**
 * @note
 * 1 : 정상
 * -2 : re-signup
 * -99 : 탈퇴
 * 0, -1, -9 : 더이상 사용하지 않음. -99와 동일하게 탈퇴
 */
export type MemberStatus = 1 | -2 | -99 | 0 | -1 | -9;

export type Member = {
  userid: number;
  status: MemberStatus;
  connectNickName: string;
  closetUserName: string;
  name: string;
  memberStatus: string;
  email: string;
  marketingAgree: boolean;
  registrationDate: string;
  marketingAgreeDate: string;
  connectMarketingAgree: boolean;
  connectMarketingAgreeDate: string;
  externalProviders: AuthProvider[];
  connectUserStatus: ConnectUserStatus;
  connectCommunityUserStatus: ConnectCommunityUserStatus;
  connectUserType: ConnectUserType;
  connectCommunityUserType: ConnectCommunityUserType;
  connectUserSupplierTypes: ConnectUserSupplierType[];
  connectBlockChainWalletAddress: Nullable<string>;
  everyWearCreatorStatus: Nullable<ConnectEverywearStatus>;
  verifyToken: string | null;
  specialBadges: {
    badgeType: ConnectAchievementBadge;
    achieve: boolean;
  }[];
  isIntegrated: boolean;
  integratedAt: string;
  connectReferees: {
    refereeUserId: number;
    refererUserId: number;
    referrerEmail: string;
    degree: number;
    type: number;
    startDateTime: string;
    endDateTime: string;
  }[];
};

export type MemberExcel = { externalProviders: string } & Omit<Member, 'externalProviders'>;

export type GetMemberExcelResponse = GetDataListResponse<MemberExcel>;

export type LoginHistory = {
  loginDate: string;
  seq: number;
  userid: number;
};

// 현재(2022/02/18) backend 에서 관리하는 Login provider
// https://clo.atlassian.net/browse/CLOSET-4066?focusedCommentId=47310
export enum LoginProvider {
  CLOSET = 0,
  FACEBOOK = 1,
  GOOGLE = 2,
  CLO = 3,
  MD = 4,
  GROUP_TOKEN = 5,
  NIKE_OKTA = 6,
  HUGOBOSS_ADFS = 105,
  ENT_DEMO_ADFS = 99999,
}

export type AuthProvider = {
  authProvider: number;
  authProviderName: string;
  providerKey: string | null;
  registrationDate: string;
};

export type Coupon = {
  amount: number;
  context: CouponReason;
  couponDesc: string;
  couponId: string;
  couponType: CouponType;
  createdBy: number;
  createdDate: string;
  expirationDate: string;
  isPublic: boolean;
  status: CouponStatus;
  userid: number;
};

export type GetMembersResponse = GetDataListResponse<Member>;
export type GetLoginHistoryResponse = GetDataListResponse<LoginHistory>;
export type GetCouponListResponse = GetDataListResponse<Coupon>;
export type GetCommentResponse = GetDataListResponse<Comment>;

export type GroupMembers = { [key in 'editor' | 'viewer' | 'collaborator']: number };

export type GetMemberGeneralInformationResponse = Member & {
  groupId: string;
  groupMembers: GroupMembers;
  groupName: string;
  isGroupTrialUsed: boolean;
};

export enum ExcelMemberExport {
  All = -1,
  CLOSET_MARKETING = 1,
  CONNECT_MARKETING = 2,
}

export type GetMemberListParams = {
  keyword?: string;
  connectUserStatus?: CheckObjectValue;
  connectCommunityUserStatus?: CheckObjectValue;
  everywearCreatorStatus: CheckObjectValue;
  connectUserType: CheckObjectValue;
  connectCommunityUserType: CheckObjectValue;
  userStatus: CheckObjectValue;
} & GetPageParams;

export type PostMemberCommentParams = {
  userId: string;
  commentText: string;
  reFetch?: boolean;
  serviceType: ServiceType;
} & GetPageParams;

export type PutMemberCommentParams = PostMemberCommentParams & {
  id: string;
  partitionKey: string;
};

export type GetCouponListParams = {
  userId: string;
} & GetPageParams;

export type CommentDetail = {
  commentText: string;
  writeAt: string;
  writeBy: string;
};

export type Comment = {
  serviceType: ServiceType;
  adminScreen: string;
  histories: CommentDetail[]; // todo
  id: string;
  keys?: { userid: string; serviceType?: ServiceType }; // ???
  partitionKey: string;
  tags: any; // todo
} & CommentDetail;

export type DeleteMemberCommentParams = Omit<
  PostMemberCommentParams,
  'commentText' | 'serviceType'
> & {
  id: string;
  partitionKey: string;
  writeBy: string;
} & GetPageParams;

export type GetBrandSettingParams = {
  userId: string;
};

export type GetBillingAddressParams = GetBrandSettingParams;

export interface BenchmarkData {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: BenchmarkListItem[];
}

export interface GetBenchmarkResponse extends Omit<BenchmarkData, 'items'> {
  items: (BenchmarkListItem & { result: string })[];
}

export type BrandSettingForm = {
  isBrand: boolean;
  showOnPage: boolean;
  file?: File;
};

export type UpdateBrandSettingParams<T = FormData> = {
  userId: string;
  formData: T;
};

export interface BenchmarkListItem {
  seq: number;
  userId: number;
  email: string;
  result: BenchmarkResult;
  registeredDate: string;
}

export interface ApiTestData {
  time: number;
}

export interface DownloadTestData {
  no?: number;
  url: string;
  progress: number;
  time: number;
  bps: string;
  cached: 'MISS' | 'HIT';
}

export interface ViewerTestData {
  no?: number;
  startTime: number;
  finishTime: number;
  progress: number;
  time: number;
  complete: boolean;
}

export interface UploadTestData {
  progress: number;
  startTime: number;
  finishTime: number;
  time: number;
  bps: string;
  status: string;
  loading: boolean;
}

export interface CFInfoData {
  region: string;
  country: string;
  timezone: string;
  city: string;
}

export interface BenchmarkResult {
  apiTestData: ApiTestData[];
  downloadData: DownloadTestData[];
  uploadData: UploadTestData;
  viewer1Data: ViewerTestData;
  viewer2Data: ViewerTestData;
  cfInfo: CFInfoData;
}

//   임시로 운영팀 사용하도록 account delete 추가함.
export interface DeleteAccountParams {
  userId: string;
  userEmail: string;
}

export interface DeleteAccountResponse {
  data: boolean;
}

export interface ExcelMemberExportParams {
  type: ExcelMemberExport;
  keyword: string;
}

export interface BrandSettingResponse {
  isBrand: boolean;
  brandImageUrl?: string;
  brandImageFileName?: string;
  showOnPage: boolean;
}

export interface BillingAddressResponse extends BillingAddress {
  userId: number;
}

export enum ConnectUserStatus {
  ACTIVE = 1,
  INACTIVE,
}

export enum ConnectCommunityUserStatus {
  ACTIVE = 1,
  INACTIVE,
}

export enum ConnectEverywearStatus {
  INACTIVE = 0, // 신청안함
  ACTIVE = 1,
}

export enum UserStatus {
  INACTIVE = 0,
  ACTIVE = 1,
}

export enum ConnectUserSupplierType {
  Fabric = 0,
  Trim = 1,
}

export type PutConnectUserStatusParams = {
  userId?: string;
  status: Nullable<number>;
};

export type PutConnectUserTypeParams = {
  userId: string;
  type: number;
  supplierTypes?: number[];
};

export interface GetEmailVerificationLinkResponse {
  userId: number;
  email: string;
  link: string;
}

export interface EmailChangeHistory {
  changedDate: string;
  previousEmail: string;
  changedEmail: string;
}
export type GetEmailChangeHistoryResponse = EmailChangeHistory[];

export enum ConnectUserType {
  GENERAL = 0,
  SUPPLIER,
  CLO_CREATOR_COLLECTIVE,
  OFFICIAL,
}

export enum ConnectCommunityUserType {
  GENERAL = 0,
  CLOVER = 100,
}

export interface ConnectInitItemProps {
  [key: string | number]: boolean;
}

export interface ConnectInitObjectProps {
  [key: string | number]: ConnectInitItemProps;
}

export enum ConnectAchievementBadge {
  COMMUNITY_HERO = 17,
  CONNECT_SUPPORTERS = 18,
}

export type PutConnectBadgeParams = {
  userId: string;
  badgeType: ConnectAchievementBadge;
  achieve: boolean;
};

export interface GetMembersManagersParams {
  pageSize: number;
  pageNo?: number;
}

export interface GetMembersManagersItem {
  id: number;
  userId: number;
  email: string;
}

export interface GetMembersManagersResponse {
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: GetMembersManagersItem[];
}

export interface GetMemberAccountDeletionStatusParams {
  userId: string;
}

export interface GetMemberAccountDeletionStatusResponse {
  canDeleteAccount: boolean;
  nonDeletableProducts: string[];
}
